import React__default, { createContext, useContext, useMemo } from 'react';
import { E as ErrorBoundary } from './vendor-4c16529b.js';
import { getTypeId } from '@sm/question-definitions';

const filterVisualizationByQuestionType = typeId => qd => {
  const [qts] = qd;
  return qts.includes(typeId);
};

const FallbackComponent = ({
  error
}) => {
  // TODO: temporary fallback ui (RAWR-773)
  return React__default.createElement(React__default.Fragment, null, React__default.createElement("h1", null, "Sorry.. there was an error"), React__default.createElement("p", null, error.message));
};

const withErrorBoundary = Component => {
  return props => React__default.createElement(ErrorBoundary, {
    FallbackComponent: FallbackComponent
  }, React__default.createElement(Component, Object.assign({}, props)));
};
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */


const declareVisualization = (questionTypes, capabilities, component) => {
  return [questionTypes, capabilities, component];
};

const providerAccessContext = createContext(null);

const useProviderAccessContext = () => {
  const access = useContext(providerAccessContext);

  if (!access) {
    throw new Error('Must call useProviderAccessContext inside a properly setup Capability Provider');
  }

  const {
    useUpstreamConfig,
    useVisualizationList
  } = access;
  return {
    useVisualizationList,
    useUpstreamConfig
  };
};
/** providerFactory
 * Creates a helper component that enables a CapabilityProvider to store the appropriate configurations,
 * making them available to the visualizations through the generic controllers. It makes sure that all
 * Visualizations have the proper types passed onto them.
 * @param visualizationList list of visualization definitions for all the visualizations that will be
 * supported by this Capability Provider.
 * @returns React Context Provider accepting the appropriate configuration for the CapabilityProvider
 */


const providerFactory = visualizationList => {
  const ctx = React__default.createContext(null); // TODO: further filter types to match the ones in the right indexes based on capability

  const useVisualizationList = capability => visualizationList.filter(([, fs]) => fs.indexOf(capability) >= 0);

  const useUpstreamConfig = () => {
    const config = useContext(ctx);

    if (!config) {
      throw new Error('Must call useUpstreamConfig inside a properly set up Capability Provider');
    }

    return config;
  };

  return ({
    children,
    value
  }) => {
    return React__default.createElement(ErrorBoundary, {
      FallbackComponent: FallbackComponent
    }, React__default.createElement(providerAccessContext.Provider, {
      value: {
        useVisualizationList: useVisualizationList,
        useUpstreamConfig: useUpstreamConfig
      }
    }, React__default.createElement(ctx.Provider, {
      value: value
    }, children)));
  };
};

const UnsupportedVisualization = ({
  question: {
    typeId
  }
}) => React__default.createElement("div", null, "Unsupported Visualization for ", typeId);

const createVisualization = (capability, questionTypeId, visualizationOptions, upstreamActions, settings, renderProps) => {
  const [,, VisualizationComponent] = visualizationOptions.find(filterVisualizationByQuestionType(questionTypeId)) || [];

  if (!VisualizationComponent) {
    return ({
      question
    }) => React__default.createElement(UnsupportedVisualization, {
      question: question
    });
  }

  return ({
    question,
    displayOptions,
    namedSlots,
    toExternalQuestion
  }) => React__default.createElement(VisualizationComponent, {
    capability: capability,
    question: question,
    upstreamActions: upstreamActions,
    settings: settings,
    displayOptions: displayOptions,
    renderProps: renderProps,
    toExternalQuestion: toExternalQuestion,
    namedSlots: namedSlots
  });
};
/** Finds and attaches a typeId to an `ExternalQuestion` */


const useExternalQuestionAugmentation = (externalQuestion, responseSet) => {
  return useMemo(() => {
    const typeId = getTypeId(externalQuestion);

    if (!typeId) {
      throw new Error(`Unsupported Question Type of family ${externalQuestion.family}, variant ${externalQuestion.variant}.`);
    }

    if (responseSet !== undefined) {
      return { ...externalQuestion,
        typeId,
        ...responseSet
      };
    }

    return { ...externalQuestion,
      typeId
    };
  }, [externalQuestion, responseSet]);
};

export { useExternalQuestionAugmentation as a, createVisualization as c, declareVisualization as d, providerFactory as p, useProviderAccessContext as u, withErrorBoundary as w };